<template>
  <section
    id="about-me-section"
    class="common-padding text-flax-smoke-200 relative z-10 overflow-y-clip rounded-b-3xl bg-[#0B0B0A] shadow-2xl will-change-auto mt-16 sm:mt-0"
  >
    <!-- Title centered, no arrow -->
    <div class="flex justify-center">
      <h3
        id="little-bit-about-me"
        v-html="aboutMe"
        class="heading-1-alt lg:heading-1 section-heading leading-none font-extrabold uppercase"
      ></h3>
    </div>

    <!-- Stacked Text Sections — centered, 2/3 width, center-aligned -->
    <div class="padding-y mt-6 mx-auto flex flex-col gap-12 w-full max-w-[66%] max-sm:max-w-full text-center">
      <p
        class="heading-4 relative w-full leading-snug font-medium text-balance"
      >
        I'm Nhi, a Computer Science student @ York University.
      </p>

      <div>
        <p class="heading-6 text-flax-smoke-300/85 mb-4">( ABOUT ME )</p>
        <p class="heading-6 font-fancy w-full text-balance text-flax-smoke-300">
          I build software, AI/ML systems + infrastructure, and perception for physical AI that bridge the gap between complex data and real-world impact. Currently, I'm building AI tooling and infrastructure at CGI, computer vision for tree-planting drones at MiON Forest, and edge perception systems at Elder Lab (York University). I'm especially drawn to autonomous systems and robotics (who doesn't love TARS? 🤖), which is why I'm building vehicles and robotics systems with student engineering teams.
          <a href="#experience-section" class="heading-6 font-fancy text-flax-smoke-100 hover:text-flax-smoke-300 transition-colors duration-300 underline decoration-dotted decoration-1 underline-offset-4 italic">
            See my work.
          </a>
        </p>
      </div>

      <div>
        <p class="heading-6 text-flax-smoke-300/85 mb-4">( WHY I BUILD )</p>
        <p class="heading-6 font-fancy w-full text-balance text-flax-smoke-300">
          Tech is my lever for change. Over the years, I've <CyclicText :options="whyIBuildOptions" />.
          <br />
          These experiences drive my commitment to equity, accessibility, and creating genuine opportunity in everything I do.
        </p>
      </div>

      <div>
        <p class="heading-6 text-flax-smoke-300/85 mb-4">( HOBBIES )</p>
        <p class="heading-6 font-fancy w-full text-balance text-flax-smoke-300">
          Away from the keyboard, I'm <CyclicText :options="hobbiesOptions" />. On campus, I'm a Tech Lead @ Google Developer Group (GDG) at York University + VP @ AI for Impact and spend my time building with our university engineering design teams.
          <a href="#community-section" class="heading-6 font-fancy text-flax-smoke-100 hover:text-flax-smoke-300 transition-colors duration-300 underline decoration-dotted decoration-1 underline-offset-4 italic">
            Read more.
          </a>
        </p>
      </div>
    </div>

    <!-- Picture Dump Collage — full width -->
    <div class="mt-16 sm:mt-20">
      <MemoryLane />
    </div>
  </section>
</template>

<script setup lang="ts">
  import {
    animateSplitText,
    animateAboutMeSectionLeave,
  } from '@/animations';
  import { onBeforeMount, onMounted, ref } from 'vue';
  import { textSplitterIntoChar } from '@/functions';
  import CyclicText from '@/components/common/CyclicText.vue';
  import MemoryLane from '@/components/common/MemoryLane.vue';

  const aboutMe = ref('About /');

  const whyIBuildOptions = [
    { text: 'built software for nonprofits (Rise and Shine Dyslexic Organization @ Uganda)', color: '#e8e8df' },
    { text: 'mentored IT for girls in remote villages in Vietnam', color: '#C4A882' },
    { text: 'organized youth leadership workshops with AIESEC', color: '#8FA88E' },
    { text: 'led programs for women in STEM', color: '#C4898A' },
  ];

  const hobbiesOptions = [
    { text: 'skateboarding', color: '#e8e8df' },
    { text: 'jamming on bass or guitar', color: '#C4A882' },
    { text: 'reading', color: '#8FA88E' },
    { text: 'bringing people together', color: '#C4898A' },
  ];

  onBeforeMount(() => {
    aboutMe.value = textSplitterIntoChar('About / ', true);
  });

  onMounted(() => {
    animateSplitText(
      '#little-bit-about-me .letters',
      '#little-bit-about-me',
      1,
      0.01,
      0,
    );

    animateAboutMeSectionLeave('#about-me-section');
  });
</script>
